import _ from "lodash";
import React, { createContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { SmallTalkNotification } from "../components/smallTalkNotification";
import { initiateSmallTalkClient, triggerMessageRecevied } from "src/legacy/redux/actions/smallTalk";
import {
  receivedSmallTalkMessage,
  appendReceivedMessageToCache,
  setSmallTalkSessionCache,
} from "src/redux/actions/smallTalk";
import { useLocation } from "react-router-dom";
import { DealRoomEngageNotification } from "src/pages/dealRoom/components/smallTalkNotification";

export interface AppContextState {
  smallTalk: {
    client: any,
    reconnectedAt?: number | null,
  };
}

export const AppContext = createContext<AppContextState>({
  smallTalk: {
    client: null,
    reconnectedAt: null,
  },
});

const AppContextProvider = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const toastIdRef = useRef<any>(null);

  const queryClient = useQueryClient();
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;


  const locationRef = useRef(location);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  // redux store data
  const engageEnabled = useSelector((state: any) => {
    return _.get(state, 'accounts.item.engageEnabled', false);
  });
  
  const user = useSelector((state: any) => {
    return _.get(state, 'users.me', null);
  });

  const authKey = useSelector((state: any) => {
    return _.get(state, 'legacyRootReducer.smallTalk.authKey', '');
  });

  const uuid = useSelector((state: any) => {
    return _.get(state, 'legacyRootReducer.smallTalk.uuid', '')
  });

  const session = useSelector((state: any) => {
    return _.get(state, 'legacyRootReducer.smallTalk.session', '');
  });
  const sessionRef = useRef(session);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const chatUsers = useSelector((state: any) => {
    return _.get(state, 'legacyRootReducer.chatUser.items', '');
  });
  const userRef = useRef(user);
  const chatUsersRef = useRef(chatUsers);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    chatUsersRef.current = chatUsers;
  }, [chatUsers]);

  useEffect(() => {
    if (authKey && uuid) {
      setSmallTalkSessionCache(queryClientRef.current, { authKey, uuid });
    }
  }, [authKey, uuid]);

  //state
  const [smallTalk, setSmallTalk] = useState<any>({
    client: null,
    reconnectedAt: null,
  });

  const isChatActive = (path, id) => {
    // Match the last segment after /engage/
    const match = path.match(/\/engage\/([^/]+)$/);

    if (!match) {
      return false;
    }

    return match[1] === id;
  }

  useEffect(() => {
    if (!authKey || !uuid) {
      setSmallTalk((prev) => ({
        ...prev,
        client: null,
      }));
      return;
    }

    const smallTalkConfig = {
      publishKey: process.env.REACT_APP_PUBNUB_PUBLISH_KEY,
      subscribeKey: process.env.REACT_APP_PUBNUB_SUBSCRIBE_KEY,
    };

    const markRecovered = () => {
      setSmallTalk((prev) => ({
        ...prev,
        reconnectedAt: Date.now(),
      }));
    };

    // @ts-ignore
    const client = new PubNub({
      ...smallTalkConfig,
      authKey: authKey,
      uuid: uuid,
      restore: true,
      autoNetworkDetection: true,
      listenToBrowserNetworkEvents: true,
      logVerbosity: false,
    });

    const listener = {
      status: (status: any) => {
        const category = status?.category;
        console.log("[PubNub status]", category, status, {timestamp: Date.now()});

        if (
          category === "PNConnectedCategory" ||
          category === "PNReconnectedCategory" ||
          category === "PNNetworkUpCategory"
        ) {
          markRecovered();
        }

        if (category === "PNAccessDeniedCategory") {
          console.warn("[PubNub status] access denied; small talk credentials may need refresh", status);
        }
      },
      message: (event: any) => {
        const { channel, message, timetoken } = event;
        const currentUser = userRef.current;

        const request = {
          ...message,
          timestamp: timetoken,
        };
        
        if(_.size(request.dealRoomId) > 0){
            if( _.includes(message.sender.associatedUsers, currentUser?.id) && !isChatActive(locationRef.current.pathname, _.get(request,"sender.chatUser"))){
              const toastId = `smalltalk-${channel}-${timetoken}`;
                toastIdRef.current = toast(
                  <DealRoomEngageNotification
                    message={request}
                    handleClose={() => toast.dismiss(toastId)}
                  />,
                  { autoClose: 3000, toastId }
                );
            }
        }

        const qc = queryClientRef.current;
        if (qc && channel != null) {
          // for small talk query
          appendReceivedMessageToCache(qc, channel, message, timetoken);
        }

        const currentChatUsers = chatUsersRef.current;
        const shouldNotify =
          _.includes(message.sender.associatedUsers, currentUser?.id) ||
          (currentChatUsers || []).some(
            (item: any) =>
              item.id === message.sender.chatUser &&
              _.get(item, "associated", false),
          );
        if (_.get(message, 'sender.type', '') != "user" && shouldNotify) {
          if (
            sessionRef.current &&
            sessionRef.current?.channel &&
            sessionRef.current?.visible &&
            sessionRef.current?.channel[0] == channel
          ) {
            dispatch(triggerMessageRecevied(channel, message));
          } else {
            const showNotificationToast = () => {
              if (!locationRef.current.pathname.includes("/engage")) {
                const toastId = `smalltalk-${channel}-${timetoken}`;
                toastIdRef.current = toast(
                  <SmallTalkNotification
                    message={request}
                    handleClose={() => toast.dismiss(toastId)}
                  />,
                  { autoClose: 3000, toastId }
                );
              }
            };
            showNotificationToast();
          }
          
        } else {
          dispatch(triggerMessageRecevied(channel, request));
        }
      },
    };

    client.addListener(listener);
    client.subscribe({
      channels: [`${uuid}.*`],
      withPresence: true,
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        client.reconnect?.();
        markRecovered();
      }
    };

    const handleOnline = () => {
      client.reconnect?.();
      markRecovered();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", handleOnline);

    setSmallTalk((prev) => ({
      ...prev,
      client,
    }));

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
      client.removeListener?.(listener);
      client.unsubscribeAll?.();
      client.stop?.();
    };
  }, [authKey, uuid, dispatch]);

  useEffect(() => {
    if (engageEnabled) {
      dispatch(initiateSmallTalkClient() as any);
    }
  }, [engageEnabled]);

  return (
    <AppContext.Provider value={{
      smallTalk,
    }}>
      {children}
    </AppContext.Provider>
  )
}

export default AppContextProvider;